/**
 * Composants et hooks partagés.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { api, apiBlob } from '../lib/api.js';

export function Spinner() {
  return (
    <div className="d-flex justify-content-center py-5">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Chargement…</span>
      </div>
    </div>
  );
}

export function ErrorBox({ message }) {
  return <div className="alert alert-danger" role="alert">{message}</div>;
}

/**
 * Test de capability tolérant à un profil incomplet. Un `me` restitué par un
 * cache antérieur, ou un composant monté par un shortcode, peut ne pas porter
 * `capabilities` : l'accès direct fait alors planter la vue entière — la même
 * classe de page blanche que les régressions 3.5.14 et 3.5.18.
 */
export function peut(me, cap) {
  return !cap || !!me?.capabilities?.includes(cap);
}

/** Groupes du profil, toujours itérable. */
export function groupesDe(me) {
  return me?.groupes || [];
}

export function OfflineNote({ result }) {
  if (!result || !result.offline) return null;
  return <div className="alert alert-warning py-2">📡 Données du cache local (hors ligne).</div>;
}

/** Texte d'introduction d'écran — deux à trois lignes au maximum. */
export function Intro({ children }) {
  return <p className="adlt-intro text-muted">{children}</p>;
}

/** Hook de lecture avec cache hors ligne. */
export function useApi(path, { cache, deps = [] } = {}) {
  const [state, setState] = useState({ loading: true });
  // Jeton de course : une réponse lente d'un chemin abandonné (filtre changé,
  // composant démonté) ne doit jamais écraser la réponse courante.
  const jeton = useRef(0);
  const reload = useCallback(() => {
    const n = ++jeton.current;
    setState({ loading: true });
    api(path, { cache }).then((r) => { if (n === jeton.current) setState({ loading: false, ...r }); });
  }, [path, cache]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps est fourni par l'appelant
  useEffect(() => { reload(); return () => { jeton.current++; }; }, deps.concat([reload]));
  return [state, reload];
}

export const BADGE_STATUT = {
  planifiee: ['bg-primary', 'Planifiée'],
  annulee: ['bg-danger', 'Annulée'],
  vacances: ['bg-secondary', 'Vacances'],
  deplacee: ['bg-warning text-dark', 'Déplacée'],
  demandee: ['bg-warning text-dark', 'En attente'],
  validee: ['bg-success', 'Validée'],
  refusee: ['bg-danger', 'Refusée'],
  annule: ['bg-secondary', 'Annulée'],
  inscrit: ['bg-success', 'Inscrit'],
  excuse: ['bg-warning text-dark', 'Excusé'],
  liste_attente: ['bg-info text-dark', "Liste d'attente"],
  recue: ['bg-info text-dark', 'Reçue'],
  en_examen: ['bg-warning text-dark', 'En examen'],
  acceptee: ['bg-success', 'Acceptée'],
  publiee: ['bg-success', 'Publiée'],
  brouillon: ['bg-secondary', 'Brouillon'],
};

export function Badge({ statut }) {
  const [cls, label] = BADGE_STATUT[statut] || ['bg-secondary', statut];
  return <span className={`badge ${cls}`}>{label}</span>;
}

/** Autocomplétion d'un membre (jamais de coordonnées) — GET /membres?q=. */
export function UserSearch({ onSelect, placeholder }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const jeton = useRef(0);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    // Le minuteur annule la requête *à venir*, pas celle déjà partie : sans
    // jeton, une réponse lente sur « ab » écrase la réponse rapide sur « abcd ».
    const n = ++jeton.current;
    const timer = setTimeout(async () => {
      const r = await api('membres?q=' + encodeURIComponent(q.trim()));
      if (n === jeton.current) setResults(r.ok ? r.data : []);
    }, 300);
    return () => clearTimeout(timer);
  }, [q]);
  return (
    <div className="position-relative">
      <input className="form-control" value={q} placeholder={placeholder || 'Rechercher un membre…'}
        onChange={(e) => setQ(e.target.value)} />
      {results.length > 0 && (
        <div className="list-group position-absolute w-100 shadow" style={{ zIndex: 30 }}>
          {results.map((u) => (
            <button type="button" key={u.id} className="list-group-item list-group-item-action"
              onClick={() => { onSelect(u); setQ(''); setResults([]); }}>
              {u.nom}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Fenêtre modale Bootstrap contrôlée. */
export function Modal({ title, onClose, children, footer }) {
  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{title}</h5>
              <button type="button" className="btn-close" onClick={onClose} aria-label="Fermer"></button>
            </div>
            <div className="modal-body">{children}</div>
            {footer && <div className="modal-footer">{footer}</div>}
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}

/**
 * Image servie par une route authentifiée (photo d'adhésion).
 *
 * `<img src="…/candidatures/12/photo">` ne fonctionne pas dans la PWA : le
 * navigateur charge l'image sans l'en-tête Authorization, et la route répond
 * 401. L'image est donc récupérée par fetch authentifié puis affichée depuis
 * un object URL, libéré au démontage.
 */
export function ImageProtegee({ url, alt = '', agrandir = false, ...props }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!url) { setSrc(null); return undefined; }
    let objectUrl = null;
    let annule = false;
    apiBlob(url).then((blob) => {
      if (annule || !blob) return;
      objectUrl = URL.createObjectURL(blob);
      setSrc(objectUrl);
    });
    return () => {
      annule = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);
  if (!src) return null;
  const img = <img src={src} alt={alt} {...props} />;
  return agrandir
    ? <a href={src} target="_blank" rel="noopener noreferrer" title="Voir en grand">{img}</a>
    : img;
}

/** Corbeille — icône « trash-can » de Font Awesome Free (CC BY 4.0). */
export function IconeCorbeille() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="14" height="14" fill="currentColor" aria-hidden="true">
      <path d="M135.2 17.7L128 32 32 32C14.3 32 0 46.3 0 64S14.3 96 32 96l384 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-96 0-7.2-14.3C307.4 6.8 296.3 0 284.2 0L163.8 0c-12.1 0-23.2 6.8-28.6 17.7zM416 128L32 128 53.2 467c1.6 25.3 22.6 45 47.9 45l245.8 0c25.3 0 46.3-19.7 47.9-45L416 128z"/>
    </svg>
  );
}

/** Modifier — icône « pen-to-square » de Font Awesome Free (CC BY 4.0). */
export function IconeCrayon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="14" height="14" fill="currentColor" aria-hidden="true">
      <path d="M441 58.9L453.1 71c9.4 9.4 9.4 24.6 0 33.9L424 134.1 377.9 88 407 58.9c9.4-9.4 24.6-9.4 33.9 0zM209.8 256.2L344 121.9 390.1 168 255.8 302.2c-2.9 2.9-6.5 5-10.4 6.1l-58.5 16.7 16.7-58.5c1.1-3.9 3.2-7.5 6.1-10.4zM373.1 25L175.8 222.2c-8.7 8.7-15 19.4-18.3 31.1l-28.6 100c-2.4 8.4-.1 17.4 6.1 23.6s15.2 8.5 23.6 6.1l100-28.6c11.8-3.4 22.5-9.7 31.1-18.3L487 138.9c28.1-28.1 28.1-73.7 0-101.8L474.9 25C446.8-3.1 401.2-3.1 373.1 25zM88 64C39.4 64 0 103.4 0 152L0 424c0 48.6 39.4 88 88 88l272 0c48.6 0 88-39.4 88-88l0-112c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 112c0 22.1-17.9 40-40 40L88 464c-22.1 0-40-17.9-40-40l0-272c0-22.1 17.9-40 40-40l112 0c13.3 0 24-10.7 24-24s-10.7-24-24-24L88 64z"/>
    </svg>
  );
}

/** Loupe — icône « magnifying-glass » de Font Awesome Free (CC BY 4.0). */
export function IconeLoupe() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="14" height="14" fill="currentColor" aria-hidden="true">
      <path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z"/>
    </svg>
  );
}

/**
 * Icônes du footer — Font Awesome Free (CC BY 4.0), SVG embarqués : la PWA
 * doit rester utilisable hors ligne, sans dépendance à une fonte distante.
 */

/** Activités — « calendar-days ». */
export function IconeCalendrier({ taille = 20 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width={taille} height={taille} fill="currentColor" aria-hidden="true">
      <path d="M128 0c13.3 0 24 10.7 24 24l0 40 144 0 0-40c0-13.3 10.7-24 24-24s24 10.7 24 24l0 40 40 0c35.3 0 64 28.7 64 64l0 16 0 48 0 256c0 35.3-28.7 64-64 64L64 512c-35.3 0-64-28.7-64-64L0 192l0-48 0-16C0 92.7 28.7 64 64 64l40 0 0-40c0-13.3 10.7-24 24-24zM48 192l0 256c0 8.8 7.2 16 16 16l320 0c8.8 0 16-7.2 16-16l0-256L48 192zm80 64l32 0c8.8 0 16 7.2 16 16l0 32c0 8.8-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16l0-32c0-8.8 7.2-16 16-16zm112 0l32 0c8.8 0 16 7.2 16 16l0 32c0 8.8-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16l0-32c0-8.8 7.2-16 16-16zm112 0l32 0c8.8 0 16 7.2 16 16l0 32c0 8.8-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16l0-32c0-8.8 7.2-16 16-16zM128 368l32 0c8.8 0 16 7.2 16 16l0 32c0 8.8-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16l0-32c0-8.8 7.2-16 16-16zm112 0l32 0c8.8 0 16 7.2 16 16l0 32c0 8.8-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16l0-32c0-8.8 7.2-16 16-16zm112 0l32 0c8.8 0 16 7.2 16 16l0 32c0 8.8-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16l0-32c0-8.8 7.2-16 16-16z"/>
    </svg>
  );
}

/** Club-house — « house ». */
export function IconeMaison({ taille = 20 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" width={taille} height={taille} fill="currentColor" aria-hidden="true">
      <path d="M575.8 255.5c0 18-15 32.1-32 32.1l-32 0 .7 160.2c0 2.7-.2 5.4-.5 8.1l0 16.2c0 22.1-17.9 40-40 40l-16 0c-1.1 0-2.2 0-3.3-.1c-1.4 .1-2.8 .1-4.2 .1L416 512l-24 0c-22.1 0-40-17.9-40-40l0-24 0-64c0-17.7-14.3-32-32-32l-64 0c-17.7 0-32 14.3-32 32l0 64 0 24c0 22.1-17.9 40-40 40l-24 0-31.9 0c-1.5 0-3-.1-4.5-.2c-1.2 .1-2.4 .2-3.6 .2l-16 0c-22.1 0-40-17.9-40-40l0-112c0-.9 0-1.9 .1-2.8l0-69.7-32 0c-18 0-32-14-32-32.1c0-9 3-17 10-24L266.4 8c7-7 15-8 22-8s15 2 21 7L564.8 231.5c8 7 12 15 11 24z"/>
    </svg>
  );
}

/** Lieu — « location-dot ». */
export function IconeLieu({ taille = 14 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width={taille} height={taille} fill="currentColor" aria-hidden="true">
      <path d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"/>
    </svg>
  );
}

/** Responsable — « user ». */
export function IconePersonne({ taille = 14 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width={taille} height={taille} fill="currentColor" aria-hidden="true">
      <path d="M224 256A128 128 0 1 0 224 0a128 128 0 1 0 0 256zm-45.7 48C79.8 304 0 383.8 0 482.3C0 498.7 13.3 512 29.7 512l388.6 0c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304l-91.4 0z"/>
    </svg>
  );
}

/** Compétition — « trophy ». */
export function IconeTrophee({ taille = 14 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" width={taille} height={taille} fill="currentColor" aria-hidden="true">
      <path d="M400 0L176 0c-26.5 0-48.1 21.8-47.1 48.2c.2 5.3 .4 10.6 .7 15.8L24 64C10.7 64 0 74.7 0 88c0 92.6 33.5 157 78.5 200.7c44.3 43.1 98.3 64.8 138.1 75.8c23.4 6.5 39.4 26 39.4 45.6c0 20.9-17 37.9-37.9 37.9L192 448c-17.7 0-32 14.3-32 32s14.3 32 32 32l192 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-26.1 0c-20.9 0-37.9-17-37.9-37.9c0-19.6 15.9-39.2 39.4-45.6c39.9-11 93.9-32.7 138.2-75.8C542.5 245 576 180.6 576 88c0-13.3-10.7-24-24-24L446.4 64c.3-5.2 .5-10.4 .7-15.8C448.1 21.8 426.5 0 400 0zM48.9 112l84.4 0c9.1 90.1 29.2 150.3 51.9 190.6c-24.9-11-50.8-26.5-73.2-48.3c-32-31.1-58-76-63-142.3zM464.1 254.3c-22.4 21.8-48.3 37.3-73.2 48.3c22.7-40.3 42.8-100.5 51.9-190.6l84.4 0c-5.1 66.3-31.1 111.2-63 142.3z"/>
    </svg>
  );
}

/** Assemblée — « people-group ». */
export function IconeAssemblee({ taille = 14 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" width={taille} height={taille} fill="currentColor" aria-hidden="true">
      <path d="M72 88a56 56 0 1 1 112 0A56 56 0 1 1 72 88zM64 245.7C54 256.9 48 271.8 48 288s6 31.1 16 42.3l0-84.7zm144.4-49.3C178.7 222.7 160 261.2 160 304c0 34.3 12 65.8 32 90.5l0 21.5c0 17.7-14.3 32-32 32l-64 0c-17.7 0-32-14.3-32-32l0-26.8C26.2 371.2 0 332.7 0 288c0-61.9 50.1-112 112-112l32 0c24 0 46.2 7.5 64.4 20.3zM448 416l0-21.5c20-24.7 32-56.2 32-90.5c0-42.8-18.7-81.3-48.4-107.7C449.8 183.5 472 176 496 176l32 0c61.9 0 112 50.1 112 112c0 44.7-26.2 83.2-64 101.2l0 26.8c0 17.7-14.3 32-32 32l-64 0c-17.7 0-32-14.3-32-32zm8-328a56 56 0 1 1 112 0A56 56 0 1 1 456 88zM576 245.7l0 84.7c10-11.3 16-26.1 16-42.3s-6-31.1-16-42.3zM320 32a64 64 0 1 1 0 128 64 64 0 1 1 0-128zM240 304c0 16.2 6 31 16 42.3l0-84.7c-10 11.3-16 26.1-16 42.3zm144-42.3l0 84.7c10-11.3 16-26.1 16-42.3s-6-31.1-16-42.3zM448 304c0 44.7-26.2 83.2-64 101.2l0 26.8c0 17.7-14.3 32-32 32l-64 0c-17.7 0-32-14.3-32-32l0-26.8c-37.8-18-64-56.5-64-101.2c0-61.9 50.1-112 112-112l32 0c61.9 0 112 50.1 112 112z"/>
    </svg>
  );
}

/** Autre — « star ». */
export function IconeEtoile({ taille = 14 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" width={taille} height={taille} fill="currentColor" aria-hidden="true">
      <path d="M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3l128.3-68.5 128.3 68.5c10.8 5.7 23.9 4.9 33.8-2.3s14.9-19.3 12.9-31.3L438.5 329 542.7 225.9c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381.2 150.3 316.9 18z"/>
    </svg>
  );
}

/** Icône du type d'activité. */
export function IconeType({ type, taille = 14 }) {
  const map = {
    competition: IconeTrophee,
    assemblee: IconeAssemblee,
    animation: IconeCible,
    initiation: IconePersonne,
    autre: IconeEtoile,
  };
  const Icone = map[type] || IconeEtoile;
  return <Icone taille={taille} />;
}

/** Mes entraînements — « bullseye » (cible de tir à l'arc). */
export function IconeCible({ taille = 20 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width={taille} height={taille} fill="currentColor" aria-hidden="true">
      <path d="M448 256A192 192 0 1 0 64 256a192 192 0 1 0 384 0zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256zm256 80a80 80 0 1 0 0-160 80 80 0 1 0 0 160zm0-224a144 144 0 1 1 0 288 144 144 0 1 1 0-288zM224 256a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z"/>
    </svg>
  );
}

export function Toast({ message, error, onDone, id }) {
  // `onDone` est recréé à chaque rendu du parent : en dépendre relançait les
  // 4 secondes à chaque re-rendu de l'application. Le minuteur ne doit courir
  // que depuis l'apparition du toast — c'est le seul canal qui signale les
  // écritures hors ligne refusées. On dépend de `id` et non de `message` :
  // deux messages identiques successifs sont bien deux toasts.
  const fin = useRef(onDone);
  fin.current = onDone;
  useEffect(() => {
    const t = setTimeout(() => fin.current(), 4000);
    return () => clearTimeout(t);
  }, [id ?? message]);
  return (
    <div className={`toast show position-fixed bottom-0 start-50 translate-middle-x mb-4 text-white ${error ? 'bg-danger' : 'bg-dark'}`}
      style={{ zIndex: 200 }} role="status" aria-live="polite">
      <div className="toast-body">{message}</div>
    </div>
  );
}
